import actionCreatorFactory from "../util/redux/action-factory";
import { reducerWithInitialState } from "../util/redux/typed-reducers";

import * as request from "../util/request";
import { env } from "../util/env";
import { ReduxDispatch, ReduxState } from "./reducers";
import {
    LoginInfoDto,
    LoginResult,
    RegisterUserDto,
    CreateLoginInfoDto,
    ChangePasswordDto,
} from "../util/DTOs/UserDtos";
import { getStorage, getSecureStorage } from "../util/storage";
import { showSnackbar } from "./snackbar";
import logger from "util/logger";
import { IPasskeyCreateResponse, IPasskeyResponse } from "util/interfaces";
import { getDeviceId } from "util/platformUtils";

/**
 * Constant to store tokens
 */
export const TOKEN_KEY = "mobipankki_access_token";
export const REFRESHTOKEN_KEY = "mobipankki_refresh_token";

/**
 * Action factory + actions
 */
const actionFactory = actionCreatorFactory("HypoMobiili/User");
export const logout = actionFactory("LOGOUT");
const loginAction = actionFactory<LoginInfoDto, LoginResult, string>("LOGIN");

const loginTimeoutAction = actionFactory<{}, number | undefined, string>(
    "LOGIN_TIMEOUT"
);
const tupasLoginAction = actionFactory<string, LoginResult, string>(
    "TUPAS_LOGIN"
);
const registerAction = actionFactory<RegisterUserDto, RegisterUserDto, string>(
    "REGISTER"
);
const createLoginAction = actionFactory<CreateLoginInfoDto, string, string>(
    "CREATE_LOGIN"
);
const changePasswordAction = actionFactory<ChangePasswordDto, string, string>(
    "CHANGE_PASSWORD"
);
const createKeyAction = actionFactory<string, null, IPasskeyCreateResponse>(
    "CREATE_KEY"
);
const validateKeyAction = actionFactory<string, string, string>("VALIDATE_KEY");
const deleteCredentialsAction = actionFactory<undefined, string, string>(
    "DELETE_CREDENTIALS"
);

export const checkShouldPromptFeedbackAction = actionFactory<undefined, boolean, string>("CHECK_PROMPT_FEEDBACK");


export function getLoginTimeout() {
    return async (dispatch: ReduxDispatch): Promise<number | undefined> => {
        try {
            dispatch(loginTimeoutAction({}));

            const result = await request.get<number>(
                `${env.APP_API_HOST}/api/user/loginTimeout`
            );

            const timeout = result === 0 ? undefined : result;

            dispatch(loginTimeoutAction.done(timeout));

            return timeout;
        } catch (error) {
            dispatch(
                loginTimeoutAction.failed(
                    "Kirjautumisyritysten hakeminen epäonnistui"
                )
            );
            return undefined;
        }
    };
}

export function login(username: string, password: string, save = true) {
    return async (dispatch: ReduxDispatch): Promise<LoginResult> => {
        try {
            const data: LoginInfoDto = {
                userName: username,
                password: password,
            };

            dispatch(loginAction(data));

            const result = await request.post<LoginResult>(
                `${env.APP_API_HOST}/api/user/login/`,
                data
            );

            dispatch(loginAction.done(result as LoginResult));
            return result as LoginResult;
        } catch (error) {
            // Loging failed
            if (error.status === 400) {
                dispatch(showSnackbar("Väärä tunnus tai salasana"));
                // Login attempts exceeded
            } else if (error.status === 429) {
                dispatch(showSnackbar(`Kirjautumisyritykset ylitetty.`));
                dispatch(loginAction.failed(`Kirjautumisyritykset ylitetty.`));
            } else {
                dispatch(showSnackbar("Kirjautuminen epäonnistui"));
                dispatch(loginAction.failed("Kirjautuminen epäonnistui"));
            }
            throw error;
        }
    };
}

export function tupasLogin(tupasHash: string) {
    return async (dispatch: ReduxDispatch): Promise<LoginResult> => {
        try {
            dispatch(tupasLoginAction(tupasHash));

            const result = await request.post<LoginResult>(
                `${env.APP_API_HOST}/api/user/tupasLogin/${tupasHash}`,
                {}
            );

            dispatch(tupasLoginAction.done(result as LoginResult));
            return result as LoginResult;
        } catch (error) {
            // Loging failed
            dispatch(showSnackbar("Kirjautuminen epäonnistui"));
            dispatch(tupasLoginAction.failed("Kirjautuminen epäonnistui"));
            throw error;
        }
    };
}

export function eidentifyLogin(loginResult: LoginResult) {
    return (dispatch: ReduxDispatch) => {
        dispatch(tupasLoginAction.done(loginResult));
    };
}

export function createPassCodeKey(code: string) {
    return async (dispatch: ReduxDispatch): Promise<IPasskeyCreateResponse> => {
        try {
            dispatch(createKeyAction(""));
            const result = await request.post<IPasskeyCreateResponse>(
                `${env.APP_API_HOST}/api/user/keycode/create`,
                {
                    code: code,
                    deviceId: getDeviceId(),
                }
            );
            dispatch(createKeyAction.done(null));
            return result;
        } catch (error) {
            dispatch(showSnackbar("Pääsykoodin luonti epäonnistui"));
            throw error;
        }
    };
}

export function validatePassCode(code: string, hash: string) {
    return async (dispatch: ReduxDispatch): Promise<string> => {
        try {
            dispatch(createKeyAction(""));
            const result = await request.post<IPasskeyResponse>(
                `${env.APP_API_HOST}/api/user/keycode`,
                {
                    code: code,
                    hash: hash,
                    deviceId: getDeviceId(),
                }
            );
            dispatch(createKeyAction.done(null));
            return result.key;
        } catch (error) {
            dispatch(showSnackbar("Pääsykoodin vahvistaminen epäonnistui"));
            throw error;
        }
    };
}

export function register(
    username: string | undefined,
    email: string,
    phoneNumber: string,
    password: string | undefined,
    verifyPassword: string | undefined,
    verificationHash: string,
    useTupas: boolean
) {
    return async (dispatch: ReduxDispatch): Promise<RegisterUserDto> => {
        try {
            const data: RegisterUserDto = {
                userName: useTupas ? undefined : username,
                email: email,
                password: useTupas ? undefined : password,
                verifyPassword: useTupas ? undefined : verifyPassword,
                verificationHash: verificationHash,
                useTupasLogin: useTupas,
                phoneNumber: phoneNumber,
            };

            dispatch(registerAction(data));

            const result = await request.post<RegisterUserDto>(
                `${env.APP_API_HOST}/api/eidentify/register`,
                data
            );

            dispatch(registerAction.done(result));
            dispatch(showSnackbar("Tunnus luotu"));

            return result;
        } catch (error) {
            // Register failed
            dispatch(showSnackbar("Rekisteröityminen epäonnistui"));
            throw error;
        }
    };
}

export function changePassword(
    password: string,
    verifyPassword: string,
    verificationHash: string
) {
    return async (dispatch: ReduxDispatch): Promise<string> => {
        try {
            const data: ChangePasswordDto = {
                newPassword: password,
                verifyNewPassword: verifyPassword,
                verificationHash: verificationHash,
            };

            dispatch(changePasswordAction(data));
            const result = await request.post<string>(
                `${env.APP_API_HOST}/api/eidentify/changePassword`,
                data
            );

            dispatch(changePasswordAction.done(result));
            dispatch(showSnackbar("Salasana vaihdettu"));
            return result;
        } catch (error) {
            dispatch(showSnackbar("Salasanan vaihtaminen epäonnistui"));
            throw error;
        }
    };
}

export function createLogin(
    username: string,
    password: string,
    verifyPassword: string,
    verificationHash: string
) {
    return async (dispatch: ReduxDispatch): Promise<string> => {
        try {
            const data: CreateLoginInfoDto = {
                userName: username,
                password: password,
                verifyPassword: password,
                verificationHash: verificationHash,
            };

            dispatch(createLoginAction(data));
            const result = await request.post<string>(
                `${env.APP_API_HOST}/api/eidentify/createLoginInfo`,
                data
            );

            dispatch(createLoginAction.done(result));
            dispatch(showSnackbar("Tunnus luotu"));
            return result;
        } catch (error) {
            dispatch(showSnackbar("Tunnuksen luonti epäonnistui"));
            throw error;
        }
    };
}
export async function logoutUser() {
    try {
        const result = await request.post<undefined>(
            `${env.APP_API_HOST}/api/user/logout/${getStorage().get(
                REFRESHTOKEN_KEY
            )}`,
            {}
        );
        return result;
    } catch (error) {
        throw error;
    }
}

export function removeLoginCredentials() {
    return async (dispatch: ReduxDispatch): Promise<string> => {
        try {
            dispatch(deleteCredentialsAction(undefined));
            const result = await request.deleteResource<string>(
                `${env.APP_API_HOST}/api/user/credentials`
            );

            dispatch(deleteCredentialsAction.done(result));
            dispatch(showSnackbar("Tunnus poistettu"));
            return result;
        } catch (error) {
            dispatch(showSnackbar("Tunnuksen poistaminen epäonnistui"));
            dispatch(deleteCredentialsAction.failed("error"));
            throw error;
        }
    };
}

export function checkShouldPromptFeedback() {
    return async (dispatch: ReduxDispatch): Promise<boolean> => {
        try {
            dispatch(checkShouldPromptFeedbackAction(undefined));
            const result = await request.get<boolean>(
                `${env.APP_API_HOST}/api/user/shouldPromptFeedback`
            );

            dispatch(checkShouldPromptFeedbackAction.done(result));
            return result;
        } catch (error) {
            dispatch(checkShouldPromptFeedbackAction.failed("error"));
            throw error;
        }
    };
}
/* Update when feedback was last given */
export function updateFeedbackDate() {
    return async (): Promise<boolean> => {
        try {
            const result = await request.get<boolean>(`${env.APP_API_HOST}/api/user/feedBackUpdate`);
            return result;
        } catch (err) {
            throw err;
        }
    };
}

/**
 * The internal state of this reducer
 */
export class UserState {
    username?: string;
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber?: string;
    isLoggedIn: boolean;
    loading: boolean;
    promptFeedback: boolean;
}

/**
 * The reducer.
 *
 * The module exports this as default
 */

const intialUserState: UserState = {
    firstName: "",
    lastName: "",
    isLoggedIn:
        getStorage().get(TOKEN_KEY) !== undefined &&
        getStorage().get(TOKEN_KEY) !== "",
    loading: false,
    promptFeedback: false
};

export const UserReducer = reducerWithInitialState(intialUserState)
    .case(loginAction, (state, action) => {
        return {
            ...state,
            username: action.payload.userName,
            isLoggedIn: false,
        };
    })
    .case(loginAction.failed, (state, action) => {
        return {
            ...state,
            username: undefined,
            isLoggedIn: false,
        };
    })
    .case(loginAction.done, (state, action) => {
        getStorage().add<string>(TOKEN_KEY, action.payload.accessToken);
        getStorage().add<string>(REFRESHTOKEN_KEY, action.payload.refreshToken);
        return {
            ...state,
            isLoggedIn: true,
        };
    })
    .case(tupasLoginAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(tupasLoginAction.done, (state, action) => {
        getStorage().add<string>(TOKEN_KEY, action.payload.accessToken);
        getStorage().add<string>(REFRESHTOKEN_KEY, action.payload.refreshToken);
        return {
            ...state,
            isLoggedIn: true,
            loading: false,
        };
    })
    .case(tupasLoginAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(deleteCredentialsAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(deleteCredentialsAction.done, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(deleteCredentialsAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    }) .case(checkShouldPromptFeedbackAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(checkShouldPromptFeedbackAction.done, (state, action) => {
        return {
            ...state,
            promptFeedback: action.payload,
            loading: false,
        };
    })
    .case(checkShouldPromptFeedbackAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    });

export default UserReducer;

/**
 * Selectors
 */



// WEBPACK FOOTER //
// ./src/ducks/user.ts